/**
 * Rendered when the app runs in the 'inCollaborate' context — i.e. the host
 * pressed "Share my view to everyone" and Zoom opened the app in the main
 * meeting window for every participant.
 *
 * Renders the live activity + results NATIVELY (same origin as the Zoom app).
 * We previously iframed the web display page (<web>/session/:id/display), but
 * Zoom's in-client webview gates embedding a second domain, so the frame was
 * blocked. Rendering in-app sidesteps that entirely. The host additionally
 * gets a "Stop sharing" overlay.
 */

import { useState, useEffect, useCallback } from 'react';
import type { SessionNode } from '../lib/api';
import {
  advanceNode,
  previousNode,
  nodeTitle,
  revealQuiz,
  quizNextQuestion,
  generateQuestionSummary,
  generateSwotSummary,
  apiErrorMessage,
} from '../lib/api';
import { stopSharing } from '../lib/zoomActions';
import { useActivityResults } from '../hooks/useActivityResults';
import {
  TimerBar,
  IdleDisplay,
  GenericActivityView,
  WaitingView,
  PollResultsView,
  NpsResultsView,
  WordCloudResultsView,
  QuestionResultsView,
  QuizResultsView,
  QuizLeaderboardView,
  SwotResultsView,
  CrossTabResultsView,
  ExamView,
  ImageActivityResultsView,
  SlideView,
} from './CollaborateActivityViews';

interface ZoomCollaborateDisplayProps {
  sessionId: string;
  isFacilitator: boolean;
  /** The session's active node (from App.tsx; updates live on node-changed). */
  activeNode: SessionNode | null;
  /** Active node position; -1 when the session is idle. */
  activeNodeIndex: number;
  /** Total node count, for prev/next bounds. */
  nodeCount: number;
  /** Updates App.tsx's active index immediately (don't wait for the socket). */
  onNodeChange: (index: number) => void;
}

export function ZoomCollaborateDisplay({
  sessionId,
  isFacilitator,
  activeNode,
  activeNodeIndex,
  nodeCount,
  onNodeChange,
}: ZoomCollaborateDisplayProps) {
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [summarising, setSummarising] = useState(false);
  const [summariseError, setSummariseError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const data = useActivityResults(sessionId, activeNode, refreshNonce);

  const isQuiz = activeNode?.type === 'quiz' && !!data.quiz;
  // While a quiz is still running (not yet on the standings screen) Next always
  // has an in-activity step to take (reveal → next question → complete), so it
  // must stay enabled even on the last session node.
  const quizHasMoreSteps = isQuiz && !data.quiz!.complete;

  // Facilitator navigation. The host's app moves to the main stage during
  // Collaborate, so the sidebar control panel is gone — these let the host
  // drive activities from the shared view. advanceNode/previousNode broadcast
  // node-changed; App.tsx's socket then updates activeNode for everyone.
  const handlePrev = useCallback(async () => {
    if (navigating || activeNodeIndex <= 0) return;
    setNavigating(true);
    try {
      const result = await previousNode(sessionId);
      onNodeChange(result.activeNodeIndex); // authoritative; don't wait for socket
    } catch (err) {
      console.error('Failed to go to previous node:', err);
    } finally {
      setNavigating(false);
    }
  }, [sessionId, activeNodeIndex, navigating, onNodeChange]);

  const handleNext = useCallback(async () => {
    if (navigating) return;

    // Quiz: drive the quiz's internal flow before moving session nodes.
    // reveal → next-question (per question) → standings → advance node.
    if (activeNode?.type === 'quiz' && data.quiz) {
      const { complete, revealed, results } = data.quiz;
      setNavigating(true);
      try {
        if (complete) {
          // On the standings screen — leave the quiz for the next activity.
          if (activeNodeIndex < nodeCount - 1) {
            const r = await advanceNode(sessionId);
            onNodeChange(r.activeNodeIndex);
          }
        } else if (!revealed) {
          await revealQuiz(results.quizId);
          setRefreshNonce((n) => n + 1);
        } else {
          // Revealed → advance to the next question, or complete the quiz on
          // the last one (which deactivates it → standings screen shows).
          await quizNextQuestion(results.quizId);
          setRefreshNonce((n) => n + 1);
        }
      } catch (err) {
        console.error('Quiz navigation failed:', err);
      } finally {
        setNavigating(false);
      }
      return;
    }

    if (activeNodeIndex >= nodeCount - 1) return;
    setNavigating(true);
    try {
      const result = await advanceNode(sessionId);
      onNodeChange(result.activeNodeIndex); // authoritative; don't wait for socket
    } catch (err) {
      console.error('Failed to advance node:', err);
    } finally {
      setNavigating(false);
    }
  }, [sessionId, activeNode, data.quiz, activeNodeIndex, nodeCount, navigating, onNodeChange]);

  // Facilitator AI summary for the active question OR SWOT/framework. Once
  // cached, the read endpoints return it for everyone on the next refresh.
  const canSummarise =
    (activeNode?.type === 'question' && !!data.question) ||
    (activeNode?.type === 'swotanalysis' && !!data.swot);
  const hasSummary =
    (activeNode?.type === 'question' && !!data.question?.summary) ||
    (activeNode?.type === 'swotanalysis' && !!data.swotSummary);

  const handleSummarise = useCallback(async () => {
    if (summarising) return;
    setSummarising(true);
    setSummariseError(null);
    try {
      if (activeNode?.type === 'question' && data.question) {
        await generateQuestionSummary(data.question.questionId);
      } else if (activeNode?.type === 'swotanalysis' && data.swot) {
        await generateSwotSummary(data.swot.swotAnalysisId);
      } else {
        return;
      }
      setRefreshNonce((n) => n + 1);
    } catch (err) {
      console.error('Summarise failed:', err);
      setSummariseError(apiErrorMessage(err));
      setTimeout(() => setSummariseError(null), 6000);
    } finally {
      setSummarising(false);
    }
  }, [summarising, activeNode, data.question, data.swot]);

  // Lift the 360px sidebar cap on #root so the shared display fills the main
  // meeting window. Scoped to this component's lifetime.
  useEffect(() => {
    const root = document.getElementById('root');
    root?.classList.add('zoom-collab-fullscreen');
    return () => root?.classList.remove('zoom-collab-fullscreen');
  }, []);

  const handleStop = async () => {
    if (stopping) return;
    setStopping(true);
    setStopError(false);
    try {
      await stopSharing();
      // Zoom closes the collaborate instance for everyone; nothing to render.
    } catch (err) {
      console.error('Failed to end collaborate:', err);
      setStopError(true);
      setStopping(false);
      setTimeout(() => setStopError(false), 3000);
    }
  };

  const renderBody = () => {
    if (!activeNode) return <IdleDisplay sessionId={sessionId} />;

    switch (activeNode.type) {
      case 'poll':
        return data.poll ? (
          <PollResultsView results={data.poll} />
        ) : (
          <WaitingView node={activeNode} message="Waiting for votes…" />
        );
      case 'nps':
        return data.nps ? (
          <NpsResultsView question={data.nps.question} results={data.nps.results} />
        ) : (
          <WaitingView node={activeNode} message="Waiting for responses…" />
        );
      case 'wordcloud':
        return data.wordCloud ? (
          <WordCloudResultsView results={data.wordCloud} />
        ) : (
          <WaitingView node={activeNode} message="Waiting for submissions…" />
        );
      case 'question':
        return data.question ? (
          <QuestionResultsView
            prompt={data.question.prompt}
            responses={data.question.responses}
            summary={data.question.summary}
          />
        ) : (
          <WaitingView node={activeNode} message="Waiting for responses…" />
        );
      case 'quiz':
        if (!data.quiz) return <WaitingView node={activeNode} message="Waiting for answers…" />;
        return data.quiz.complete ? (
          <QuizLeaderboardView results={data.quiz.results} />
        ) : (
          <QuizResultsView
            currentIndex={data.quiz.currentIndex}
            revealed={data.quiz.revealed}
            correctOptionId={data.quiz.correctOptionId}
            results={data.quiz.results}
          />
        );
      case 'swotanalysis':
        return data.swot ? (
          <SwotResultsView results={data.swot} summary={data.swotSummary} />
        ) : (
          <WaitingView node={activeNode} message="Waiting for contributions…" />
        );
      case 'crosstab':
        return data.crosstab ? (
          <CrossTabResultsView results={data.crosstab} />
        ) : (
          <WaitingView node={activeNode} message="Waiting for responses…" />
        );
      case 'exam':
        return data.exam ? (
          <ExamView title={data.exam.title} totalQuestions={data.exam.totalQuestions} />
        ) : (
          <WaitingView node={activeNode} message="Loading…" />
        );
      case 'imageactivity':
        return data.imageActivity ? (
          <ImageActivityResultsView results={data.imageActivity} />
        ) : (
          <WaitingView node={activeNode} message="Loading image…" />
        );
      case 'slide':
        return <SlideView slideId={activeNode.id} title={nodeTitle(activeNode)} />;
      default:
        // any future type with no native view yet.
        return <GenericActivityView node={activeNode} subtitle="This activity is live" />;
    }
  };

  return (
    <div className="relative w-full h-screen bg-gray-900 flex flex-col">
      {data.timer && (
        <TimerBar expiresAt={data.timer.expiresAt} durationMinutes={data.timer.durationMinutes} />
      )}
      <div className="flex-1 min-h-0">{renderBody()}</div>

      {isFacilitator && (
        <button
          onClick={handleStop}
          disabled={stopping}
          className={`absolute top-3 right-3 px-3 py-1.5 text-xs font-medium rounded-lg
            shadow-md transition-colors disabled:opacity-60 ${
              stopError
                ? 'bg-red-500 text-white'
                : 'bg-white/90 text-gray-700 hover:bg-white'
            }`}
        >
          {stopError ? 'Failed — try again' : stopping ? 'Stopping…' : 'Stop sharing'}
        </button>
      )}

      {/* Facilitator: AI-summarise an open question or SWOT/framework. */}
      {isFacilitator && canSummarise && (
        <div className="absolute top-3 left-3 flex flex-col items-start gap-1 max-w-[60%]">
          <button
            onClick={handleSummarise}
            disabled={summarising}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg shadow-md
              disabled:opacity-60 transition-colors ${
                summariseError
                  ? 'bg-red-500 text-white'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
          >
            {summarising ? 'Summarising…' : hasSummary ? 'Re-summarise' : 'Summarise'}
          </button>
          {summariseError && (
            <span className="text-[11px] text-red-100 bg-gray-900/85 rounded px-2 py-1 leading-snug">
              {summariseError}
            </span>
          )}
        </div>
      )}

      {/* Facilitator control bar — only the host sees it; participants see a
          clean display. Lets the host advance activities from the shared view. */}
      {isFacilitator && (
        <div className="flex items-center gap-3 px-4 py-3 bg-gray-800/95 border-t border-white/10">
          <button
            onClick={handlePrev}
            disabled={navigating || activeNodeIndex <= 0}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg
              bg-white/10 text-white hover:bg-white/20 disabled:opacity-30
              disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Prev
          </button>

          <div className="flex-1 min-w-0 text-center">
            <p className="text-white text-sm font-medium truncate">
              {activeNode ? nodeTitle(activeNode) : 'Not started'}
            </p>
            <p className="text-gray-400 text-xs">
              {activeNodeIndex >= 0 ? `${activeNodeIndex + 1} / ${nodeCount}` : `0 / ${nodeCount}`}
            </p>
          </div>

          <button
            onClick={handleNext}
            disabled={navigating || (!quizHasMoreSteps && activeNodeIndex >= nodeCount - 1)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg
              bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-30
              disabled:cursor-not-allowed transition-colors"
          >
            {isQuiz && data.quiz && !data.quiz.complete && !data.quiz.revealed ? 'Reveal' : 'Next'}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
